import React from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import { SERVICES, ACUITY } from "@/data/site";
import { serviceSchema, faqSchema, breadcrumb } from "@/data/schema";
import NotFound from "@/pages/NotFound";

function List({ items }) {
  return (
    <ul className="mt-4 space-y-2.5">
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-3 text-muted-ivory">
          <Check size={18} className="mt-0.5 shrink-0 text-gold" />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  return (
    <>
      <Seo
        title={`${service.title} in Vero Beach, FL | Golden Touch Lash & Brow`}
        description={service.short}
        path={`/services/${service.slug}`}
        image={service.work}
        jsonLd={[
          serviceSchema(service),
          faqSchema(service.faqs),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: service.title, path: `/services/${service.slug}` },
          ]),
        ]}
      />

      {/* Breadcrumb + hero */}
      <section className="bg-onyx pt-28 md:pt-36 pb-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-muted-ivory">
            <Link to="/" className="hover:text-gold">Home</Link>
            <ChevronRight size={13} className="text-gold/60" />
            <Link to="/services" className="hover:text-gold">Services</Link>
            <ChevronRight size={13} className="text-gold/60" />
            <span className="text-gold">{service.title}</span>
          </nav>

          <div className="mt-8 grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <h1 className="font-display text-4xl md:text-5xl text-ivory leading-tight">
                {service.title}
              </h1>
              <p className="mt-3 text-[0.72rem] tracking-[0.28em] uppercase text-gold">
                {service.tagline}
              </p>
              <p className="mt-6 text-base leading-relaxed text-muted-ivory">{service.intro}</p>
              <Link
                to={service.bookAllCategories ? "/book" : `/book?category=${encodeURIComponent(service.acuityCategory)}`}
                className="btn-gold mt-8 inline-flex px-8 py-3.5 text-[0.72rem] tracking-[0.24em] uppercase"
              >
                Book {service.title}
              </Link>
            </div>
            <div className="img-zoom overflow-hidden rounded-2xl border border-gold/15 aspect-[4/3]">
              <img
                src={service.work}
                alt={service.alt}
                width={1200}
                height={800}
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="bg-charcoal py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-3">
          <div>
            <h2 className="font-display text-2xl text-ivory">Available Services</h2>
            <p className="mt-2 text-sm text-muted-ivory">
              Tap a service to book it - you'll go straight to scheduling for {service.title}.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {service.options.map((o) => {
                const typeId = ACUITY.serviceTypeIds[o];
                const catOverride = ACUITY.serviceCategoryOverrides[o];
                const to = typeId
                  ? `/book?appointmentType=${encodeURIComponent(typeId)}`
                  : `/book?category=${encodeURIComponent(catOverride || service.acuityCategory)}`;
                return (
                  <Link
                    key={o}
                    to={to}
                    className="border border-gold/40 px-4 py-1.5 text-[0.7rem] tracking-[0.2em] uppercase text-gold transition-colors hover:bg-gold hover:text-onyx"
                  >
                    {o}
                  </Link>
                );
              })}
            </div>
            <h2 className="mt-10 font-display text-2xl text-ivory">Who It&rsquo;s For</h2>
            <p className="mt-4 text-muted-ivory leading-relaxed">{service.forWho}</p>
          </div>
          <div>
            <h2 className="font-display text-2xl text-ivory">Before Your Visit</h2>
            <List items={service.prep} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-ivory">Aftercare</h2>
            <List items={service.aftercare} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      {service.faqs && service.faqs.length > 0 && (
        <section className="bg-onyx py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-6 lg:px-10">
            <h2 className="text-center font-display text-3xl md:text-4xl text-ivory">
              Frequently Asked Questions
            </h2>
            <div className="mt-10 divide-y divide-gold/15 border-y border-gold/15">
              {service.faqs.map((f, i) => (
                <details key={i} className="group py-5">
                  <summary className="flex cursor-pointer items-center justify-between gap-4 text-left text-ivory">
                    <span className="font-display text-lg">{f.q}</span>
                    <ChevronRight
                      size={18}
                      className="shrink-0 text-gold transition-transform group-open:rotate-90"
                    />
                  </summary>
                  <p className="mt-3 text-muted-ivory leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="bg-charcoal py-16 text-center">
        <div className="mx-auto max-w-2xl px-6">
          <h2 className="font-display text-3xl md:text-4xl text-ivory">Ready to book?</h2>
          <p className="mt-4 text-muted-ivory">
            Reserve your {service.title.toLowerCase()} appointment online in just a few clicks.
          </p>
          <Link
            to={service.bookAllCategories ? "/book" : `/book?category=${encodeURIComponent(service.acuityCategory)}`}
            className="btn-gold mt-8 inline-flex px-10 py-3.5 text-[0.72rem] tracking-[0.24em] uppercase"
          >
            Book Your Appointment
          </Link>
        </div>
      </section>
    </>
  );
}
